/**
 * MasteringTonalDrift — «Тембр по ходу трека»: плавная правка середины, верха
 * и воздуха, которая начинается с заданного момента и нарастает к концу
 * трека (рампа во времени). Плюс замер того, как баланс этих полос меняется
 * по ходу трека, — для графика и для кнопки «По замеру».
 *
 * Зачем (письмо Премиум-3046, 08.10.2026): «У SUNO часто треки после 1:20
 * начинают уплывать на средних и высоких частотах вверх, и эту корректировку
 * я не могу реализовать через ваши инструменты». Он прав: стиль, правка тона
 * и подгонка под эталон меняют весь трек одинаково, а динамический
 * эквалайзер сравнивает момент с соседними ±4 с — медленный уход уезжает
 * вместе с его точкой отсчёта.
 *
 * ПОЧЕМУ РУЧНАЯ РАМПА, А НЕ АВТОМАТ (замер 08–09.10, разбор —
 * docs/mastering-tonal-drift.md). Девять способов автоматически отделить
 * уход тембра от аранжировки (следящее среднее 15–90 с, нижняя огибающая,
 * рампа по полосам и общая, интеграл наклона, регрессия по содержанию,
 * сравнение повторов по хроме, монотонная регрессия) проверены на 67 живых
 * треках (CC, MTG-Jamendo) с вживлённым дрейфом известной величины. Ни один не
 * лучше, чем «ничего не делать»: у живой музыки баланс верха, сглаженный за
 * 30 с, гуляет на ~2.6 дБ сам — столько же, сколько дрейф Suno. Автомат
 * портил бы чужие треки. Поиск момента начала цеплялся за аутро (ошибка
 * 60 с в медиане). Поэтому момент начала и величину решает человек — он
 * дрейф слышит; модуль меряет, показывает и точно применяет.
 *
 * «ПО ЗАМЕРУ» при известном начале честно отвечает на вопрос «насколько после
 * этого момента ярче, чем до него» — рампа, подогнанная к балансу полос:
 * только громкие места (≥ 40-го процентиля громкости — у тихих вступлений
 * и аутро свой состав), устойчивая подгонка (Хьюбер), форма по частоте —
 * гладкая (квадрат по логарифму частоты). Это описание трека, а не догадка
 * о том, что в нём дефект.
 *
 * ОБРАБОТКА — три фильтра, усиление которых меняется во времени:
 *   середина — пик 2 кГц, Q 0.7 (1–4 кГц);
 *   верх     — пик 6.3 кГц, Q 1.0 (4–10 кГц);
 *   воздух   — полка 10 кГц (S = 1).
 * Усиление = смещение + величина × r(t), r = 0 до «начала», линейно до
 * «полностью к», дальше 1. Буферный путь ставит обычные BiquadFilterNode с
 * автоматизацией gain (addToGraph), потоковый — стадию с теми же формулами
 * RBJ, что у Web Audio (makeStage); до «начала» звук бит-в-бит исходный.
 *
 * ПОДГОНКА ПОД ЭТАЛОН и тон «Авто» решаются по СРЕДНЕМУ спектру трека. Если
 * рампа срежет верх второй половины, среднее станет темнее, чем решила
 * подгонка, — верх срезан дважды. Поэтому при активной подгонке рампа
 * получает постоянное смещение (averageOffset): среднее по треку остаётся
 * тем, что решила подгонка, а по времени тембр выравнивается.
 *
 * API:
 *   resolve({t0, t1, mid, high, air}, durSec) → параметры или null (выключено)
 *   withOffsets(P, durSec)                    → P со смещением под подгонку
 *   addToGraph(ctx, link, P)                  — буферный путь (OfflineAudioContext)
 *   makeStage(sr, nCh, P)                     — потоковый путь, {process(chans)}
 *   analyzeAsync(buffer, {signal})            → замер по секундам
 *   suggest(an, t0, t1)                       → {ok, gains, rise, ...}
 *   curves(an, t0, P)                         → линии графика
 *   drawCurves(canvas, curves, opts)          → рисует их (мастеринг и /mix-analyzer)
 *   ZONES, LIMITS, DEFAULT_T0
 */
(function (root) {
    'use strict';

    var ZONES = [
        { key: 'mid',  type: 'peaking',   freq: 2000,  q: 0.7, lo: 1000,  hi: 4000 },
        { key: 'high', type: 'peaking',   freq: 6300,  q: 1.0, lo: 4000,  hi: 10000 },
        { key: 'air',  type: 'highshelf', freq: 10000, q: 0,   lo: 10000, hi: 16000 }
    ];
    var LIMITS = { min: -8, max: 4, step: 0.5 };
    // Suno: «после 1:20 начинают уплывать» — начальная точка, которую человек двигает.
    var DEFAULT_T0 = 80;
    var MIN_DUR = 45;                 // короче — нечего сравнивать «до» и «после»
    var EPS_DB = 0.25;                // меньше — стадия не создаётся
    var STEP = 16;                    // через сколько сэмплов пересчитывать коэффициенты

    function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
    function num(v, d) { v = +v; return isFinite(v) ? v : d; }
    function roundStep(v) { return Math.round(v / LIMITS.step) * LIMITS.step; }

    // ── параметры ────────────────────────────────────────────────────────
    function resolve(p, durSec) {
        if (!p) return null;
        var dur = num(durSec, 0);
        if (!(dur > 0)) return null;
        var t0 = clamp(num(p.t0, DEFAULT_T0), 0, Math.max(0, dur - 5));
        var t1 = num(p.t1, dur);
        if (!(t1 > t0 + 2)) t1 = dur;
        t1 = clamp(t1, t0 + 2, Math.max(t0 + 2, dur));
        var gains = {}, any = false;
        for (var i = 0; i < ZONES.length; i++) {
            var k = ZONES[i].key, g = clamp(num(p[k], 0), LIMITS.min, LIMITS.max);
            gains[k] = g;
            if (Math.abs(g) >= EPS_DB) any = true;
        }
        if (!any) return null;
        return { t0: t0, t1: t1, gains: gains, offsets: { mid: 0, high: 0, air: 0 }, dur: dur };
    }

    function rampAt(P, t) {
        if (t <= P.t0) return 0;
        if (t >= P.t1) return 1;
        return (t - P.t0) / (P.t1 - P.t0);
    }

    /* Смещение под подгонку к эталону: средняя по времени мощность, которую
       рампа уберёт в каждой зоне, возвращается постоянным усилением — среднее
       по треку не меняется. Время взвешено равномерно (а не по громкости):
       разница с точным весом — десятые доли дБ, а замер громкости по зонам
       потоковому пути без декода недоступен. */
    function averageOffset(P, durSec) {
        var dur = num(durSec, P.dur), off = {};
        var n = Math.max(1, Math.ceil(dur / 0.5));
        for (var i = 0; i < ZONES.length; i++) {
            var k = ZONES[i].key, g = P.gains[k], acc = 0;
            for (var j = 0; j < n; j++) {
                var t = (j + 0.5) * dur / n;
                acc += Math.pow(10, g * rampAt(P, t) / 10);
            }
            off[k] = -10 * Math.log10(acc / n);
        }
        return off;
    }
    function withOffsets(P, durSec) {
        if (!P) return P;
        var o = averageOffset(P, durSec);
        return { t0: P.t0, t1: P.t1, gains: P.gains, offsets: o, dur: P.dur };
    }

    // ── RBJ с соглашениями Web Audio (как rbjCoeffs потокового пайплайна) ─
    function rbj(type, sr, f0, q, gainDb) {
        var w = 2 * Math.PI * f0 / sr, cw = Math.cos(w), sw = Math.sin(w), A, b0, b1, b2, a0, a1, a2;
        if (type === 'peaking') {
            A = Math.pow(10, gainDb / 40);
            var al = sw / (2 * q);
            b0 = 1 + al * A; b1 = -2 * cw; b2 = 1 - al * A;
            a0 = 1 + al / A; a1 = -2 * cw; a2 = 1 - al / A;
        } else {                                    // highshelf, S = 1
            A = Math.pow(10, gainDb / 40);
            var alS = sw / 2 * Math.sqrt(2), tsa = 2 * Math.sqrt(A) * alS, Ap1 = A + 1, Am1 = A - 1;
            b0 = A * (Ap1 + Am1 * cw + tsa); b1 = -2 * A * (Am1 + Ap1 * cw); b2 = A * (Ap1 + Am1 * cw - tsa);
            a0 = Ap1 - Am1 * cw + tsa; a1 = 2 * (Am1 - Ap1 * cw); a2 = Ap1 - Am1 * cw - tsa;
        }
        return { b0: b0 / a0, b1: b1 / a0, b2: b2 / a0, a1: a1 / a0, a2: a2 / a0 };
    }
    /* АЧХ каскада — у хозяина этой математики: MasteringReference.responseCurve
       (те же соглашения Web Audio; его МНК-подгонка под эталон — та же задача,
       что «По замеру», и своя копия формулы здесь была бы пятой —
       EqHeadroomSinglePrimitiveTest). Модуль эталона грузится на обеих
       страницах раньше этого. null — модуля нет. */
    function responseDb(sr, gains, freqs) {
        var MR = root.MasteringReference;
        if (!MR || !MR.responseCurve) return null;
        var bands = [];
        for (var i = 0; i < ZONES.length; i++) {
            var z = ZONES[i], g = gains[z.key] || 0;
            if (Math.abs(g) > 1e-9) bands.push({ type: z.type, freq: z.freq, q: z.q || 1, gainDb: g });
        }
        return MR.responseCurve(bands, sr, freqs).db;
    }
    // Суммарная АЧХ трёх фильтров на частоте f при усилениях gains.
    function cascadeDb(sr, gains, f) {
        var r = responseDb(sr, gains, [f]);
        return r ? r[0] : 0;
    }

    // ── буферный путь: узлы Web Audio с автоматизацией ───────────────────
    function addToGraph(ctx, link, P) {
        if (!P) return 0;
        var made = 0;
        for (var i = 0; i < ZONES.length; i++) {
            var z = ZONES[i], g = P.gains[z.key] || 0, off = (P.offsets && P.offsets[z.key]) || 0;
            if (Math.abs(g) < 1e-9 && Math.abs(off) < 1e-9) continue;
            var bq = ctx.createBiquadFilter();
            bq.type = z.type; bq.frequency.value = z.freq;
            if (z.type === 'peaking') bq.Q.value = z.q;
            bq.gain.setValueAtTime(off, 0);
            if (P.t0 > 0) bq.gain.setValueAtTime(off, P.t0);
            bq.gain.linearRampToValueAtTime(off + g, P.t1);
            link(bq);
            made++;
        }
        return made;
    }

    // ── потоковый путь: те же формулы, состояние живёт между кусками ─────
    function makeStage(sr, nCh, P) {
        var filt = [];
        for (var i = 0; i < ZONES.length; i++) {
            var z = ZONES[i], g = P.gains[z.key] || 0, off = (P.offsets && P.offsets[z.key]) || 0;
            if (Math.abs(g) < 1e-9 && Math.abs(off) < 1e-9) continue;
            filt.push({ z: z, g: g, off: off, s1: new Float64Array(nCh), s2: new Float64Array(nCh) });
        }
        var pos = 0;                                // абсолютный номер сэмпла от начала трека
        var t0s = Math.round(P.t0 * sr);
        return {
            process: function (chans) {
                var n = chans[0].length;
                for (var fi = 0; fi < filt.length; fi++) {
                    var F = filt[fi];
                    // Блоки по STEP — по АБСОЛЮТНЫМ номерам сэмплов: коэффициенты
                    // зависят только от места в треке, а не от того, как трек
                    // нарезан на куски (потоковый и буферный пути режут по-разному).
                    var b = 0;
                    while (b < n) {
                        var abs = pos + b, blk = Math.floor(abs / STEP);
                        var e = Math.min(n, (blk + 1) * STEP - pos);
                        // До «начала» без смещения фильтр — тождество с нулевым
                        // состоянием: пропуск бит-в-бит равен прогону.
                        if (F.off === 0 && (blk + 1) * STEP <= t0s) { b = e; continue; }
                        var gDb = F.off + F.g * rampAt(P, (blk + 0.5) * STEP / sr);
                        var c = rbj(F.z.type, sr, F.z.freq, F.z.q, gDb);
                        for (var ch = 0; ch < chans.length; ch++) {
                            var d = chans[ch], s1 = F.s1[ch], s2 = F.s2[ch];
                            for (var j = b; j < e; j++) {
                                var x = d[j], y = c.b0 * x + s1;
                                s1 = c.b1 * x - c.a1 * y + s2;
                                s2 = c.b2 * x - c.a2 * y;
                                d[j] = y;
                            }
                            F.s1[ch] = s1; F.s2[ch] = s2;
                        }
                        b = e;
                    }
                }
                pos += n;
                return chans;
            }
        };
    }

    // ── замер: баланс полос по секундам ──────────────────────────────────
    // 1/3 октавы от 1 кГц до 16 кГц: всё, что правят три фильтра.
    var CENTERS = [];
    for (var ci = 12; ci < 25; ci++) CENTERS.push(63 * Math.pow(2, ci / 3));
    var LOGF = CENTERS.map(function (f) { return Math.log(f / 1000) / Math.LN2; });

    function makeFFT(N) {
        var lv = Math.round(Math.log(N) / Math.LN2), rev = new Uint32Array(N), i;
        for (i = 0; i < N; i++) { var r = 0, x = i; for (var b = 0; b < lv; b++) { r = (r << 1) | (x & 1); x >>= 1; } rev[i] = r; }
        var cs = new Float64Array(N / 2), sn = new Float64Array(N / 2);
        for (i = 0; i < N / 2; i++) { cs[i] = Math.cos(2 * Math.PI * i / N); sn[i] = -Math.sin(2 * Math.PI * i / N); }
        return function (re, im) {
            for (var a = 0; a < N; a++) { var j = rev[a]; if (j > a) { var t = re[a]; re[a] = re[j]; re[j] = t; t = im[a]; im[a] = im[j]; im[j] = t; } }
            for (var size = 2; size <= N; size <<= 1) {
                var half = size >> 1, step = N / size;
                for (var st = 0; st < N; st += size) {
                    for (var k = 0; k < half; k++) {
                        var wr = cs[k * step], wi = sn[k * step], p = st + k, q = p + half;
                        var tr = re[q] * wr - im[q] * wi, ti = re[q] * wi + im[q] * wr;
                        re[q] = re[p] - tr; im[q] = im[p] - ti; re[p] += tr; im[p] += ti;
                    }
                }
            }
        };
    }

    function abortErr() { var e = new Error('MasteringTonalDrift aborted'); e.name = 'AbortError'; return e; }
    function yielder() { return new Promise(function (r) { setTimeout(r, 0); }); }

    /* Один проход по треку: середина (L+R)/2, окно Ханна 4096 без перекрытия,
       мощность в 13 полосах и во всей полосе 50 Гц – 16.5 кГц, сведённая к
       секундам. Памяти — 14 чисел на секунду трека. */
    async function analyzeAsync(buffer, opts) {
        opts = opts || {};
        var signal = opts.signal || null;
        var sr = buffer.sampleRate, nCh = buffer.numberOfChannels, len = buffer.length;
        var L = buffer.getChannelData(0), R = nCh > 1 ? buffer.getChannelData(1) : null;
        var N = 4096, H = 4096, fft = makeFFT(N);
        var win = new Float64Array(N);
        for (var i = 0; i < N; i++) win[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / N);
        var binHz = sr / N, NB = CENTERS.length;
        var bLo = [], bHi = [];
        for (var k = 0; k < NB; k++) {
            bLo.push(Math.max(1, Math.round(CENTERS[k] * Math.pow(2, -1 / 6) / binHz)));
            bHi.push(Math.min(N / 2, Math.round(CENTERS[k] * Math.pow(2, 1 / 6) / binHz)));
        }
        var tLo = Math.round(50 / binHz), tHi = Math.min(N / 2, Math.round(16500 / binHz));
        var nSec = Math.max(1, Math.floor(len / sr));
        var bandAcc = new Float64Array(nSec * NB), totAcc = new Float64Array(nSec), cnt = new Float64Array(nSec);
        var re = new Float64Array(N), im = new Float64Array(N);
        var frames = Math.max(0, Math.floor((len - N) / H) + 1);
        for (var f = 0; f < frames; f++) {
            var off = f * H;
            for (i = 0; i < N; i++) {
                var v = R ? 0.5 * (L[off + i] + R[off + i]) : L[off + i];
                re[i] = v * win[i]; im[i] = 0;
            }
            fft(re, im);
            var sec = Math.min(nSec - 1, Math.floor((off + N / 2) / sr));
            var tot = 0;
            for (i = tLo; i < tHi; i++) tot += re[i] * re[i] + im[i] * im[i];
            totAcc[sec] += tot; cnt[sec]++;
            for (k = 0; k < NB; k++) {
                var s = 0;
                for (i = bLo[k]; i < bHi[k]; i++) s += re[i] * re[i] + im[i] * im[i];
                bandAcc[sec * NB + k] += s;
            }
            if ((f & 255) === 255) {
                if (signal && signal.aborted) throw abortErr();
                await yielder();
            }
        }
        var bandDb = new Float32Array(nSec * NB), totDb = new Float32Array(nSec);
        for (var t = 0; t < nSec; t++) {
            var c = Math.max(1, cnt[t]);
            totDb[t] = 10 * Math.log10(totAcc[t] / c + 1e-20);
            for (k = 0; k < NB; k++) bandDb[t * NB + k] = 10 * Math.log10(bandAcc[t * NB + k] / c + 1e-20);
        }
        return finishAnalysis(bandDb, totDb, nSec, sr, len / sr);
    }

    function percentile(arr, q) {
        var a = Array.prototype.slice.call(arr).sort(function (x, y) { return x - y; });
        if (!a.length) return 0;
        var p = clamp(q, 0, 1) * (a.length - 1), lo = Math.floor(p), hi = Math.ceil(p);
        return a[lo] + (a[hi] - a[lo]) * (p - lo);
    }

    function finishAnalysis(bandDb, totDb, nSec, sr, dur) {
        var top = percentile(totDb, 0.95), valid = new Uint8Array(nSec), vals = [];
        for (var t = 0; t < nSec; t++) if (totDb[t] > top - 25) { valid[t] = 1; vals.push(totDb[t]); }
        var loudThr = percentile(vals, 0.40), loud = new Uint8Array(nSec);
        var s = -1, e = -1;
        for (t = 0; t < nSec; t++) if (valid[t]) { if (s < 0) s = t; e = t; if (totDb[t] >= loudThr) loud[t] = 1; }
        return { nSec: nSec, nb: CENTERS.length, bandDb: bandDb, totDb: totDb, valid: valid, loud: loud,
                 start: Math.max(0, s), end: Math.max(0, e), dur: dur, sr: sr };
    }

    // Баланс зоны в секунду t: мощность её полос относительно всей полосы.
    function zoneBalance(an, zi, t) {
        var z = ZONES[zi], NB = an.nb, s = 0;
        for (var k = 0; k < NB; k++) {
            if (CENTERS[k] >= z.lo * 0.89 && CENTERS[k] < z.hi * 1.12) s += Math.pow(10, an.bandDb[t * NB + k] / 10);
        }
        return 10 * Math.log10(s + 1e-20) - an.totDb[t];
    }

    /* «По замеру»: рампа 0 → 1 между t0 и t1, подогнанная ко всем 13 полосам
       сразу. Возвращает усиления фильтров, которые эту рампу уберут. */
    function suggest(an, t0, t1, opts) {
        if (!an || an.dur < MIN_DUR) return { ok: false, reason: 'short' };
        var NB = an.nb, s = an.start, e = an.end;
        t0 = num(t0, DEFAULT_T0); t1 = num(t1, an.dur);
        if (!(t1 > t0 + 2)) t1 = an.dur;
        var rows = [], rr = [];
        var before = 0, after = 0;
        for (var t = s + 5; t <= e - 8; t++) {
            if (!an.loud[t]) continue;
            var r = clamp((t + 0.5 - t0) / Math.max(1, t1 - t0), 0, 1);
            rows.push(t); rr.push(r);
            if (r <= 0) before++; else after++;
        }
        if (before < 15 || after < 12) return { ok: false, reason: before < 15 ? 'short_before' : 'short_after' };
        var M = rows.length, k, i, it;
        var w = new Float64Array(M); for (i = 0; i < M; i++) w[i] = 1;
        var cK = new Float64Array(NB), sK = new Float64Array(NB);
        for (it = 0; it < 4; it++) {
            // взвешенный МНК y = c + s·r для каждой полосы (общие веса)
            var Sw = 0, Sr = 0, Srr = 0;
            for (i = 0; i < M; i++) { var ww = w[i] * w[i]; Sw += ww; Sr += ww * rr[i]; Srr += ww * rr[i] * rr[i]; }
            var det = Sw * Srr - Sr * Sr;
            if (Math.abs(det) < 1e-12) return { ok: false, reason: 'flat' };
            for (k = 0; k < NB; k++) {
                var Sy = 0, Sry = 0;
                for (i = 0; i < M; i++) { var w2 = w[i] * w[i], y = an.bandDb[rows[i] * NB + k] - an.totDb[rows[i]]; Sy += w2 * y; Sry += w2 * rr[i] * y; }
                cK[k] = (Srr * Sy - Sr * Sry) / det;
                sK[k] = (Sw * Sry - Sr * Sy) / det;
            }
            // Хьюбер: блок, далёкий от модели (соло, брейк), весит меньше
            for (i = 0; i < M; i++) {
                var res = 0;
                for (k = 0; k < NB; k++) res += Math.abs(an.bandDb[rows[i] * NB + k] - an.totDb[rows[i]] - cK[k] - sK[k] * rr[i]);
                res /= NB;
                w[i] = Math.sqrt(Math.min(1, 1.5 / (res + 1e-9)));
            }
        }
        // гладкая форма по частоте: квадрат по log2(f/1 кГц)
        var shape = (opts && opts.shape === 'raw') ? Array.prototype.slice.call(sK) : smoothQuad(sK);
        // усиления трёх фильтров: МНК против −shape на центрах полос
        var gains = fitGains(shape.map(function (v) { return -v; }), an.sr || 48000);
        if (!gains) return { ok: false, reason: 'no_response_math' };
        var rise = {};
        for (var zi = 0; zi < ZONES.length; zi++) {
            var z = ZONES[zi], acc = 0, nZ = 0;
            for (k = 0; k < NB; k++) if (CENTERS[k] >= z.lo * 0.89 && CENTERS[k] < z.hi * 1.12) { acc += shape[k]; nZ++; }
            rise[z.key] = nZ ? acc / nZ : 0;
        }
        return { ok: true, t0: t0, t1: t1, gains: gains, rise: rise, shape: shape };
    }

    function smoothQuad(v) {
        // МНК a + b·x + c·x² по 13 точкам; нормальные уравнения 3×3
        var S = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], B = [0, 0, 0];
        for (var k = 0; k < v.length; k++) {
            var x = LOGF[k], ph = [1, x, x * x];
            for (var a = 0; a < 3; a++) { B[a] += ph[a] * v[k]; for (var b = 0; b < 3; b++) S[a][b] += ph[a] * ph[b]; }
        }
        var c = solve3(S, B);
        return v.map(function (_, k2) { var x2 = LOGF[k2]; return c[0] + c[1] * x2 + c[2] * x2 * x2; });
    }
    function solve3(A, b) {
        var m = [A[0].concat([b[0]]), A[1].concat([b[1]]), A[2].concat([b[2]])], i, j, r;
        for (i = 0; i < 3; i++) {
            var p = i; for (r = i + 1; r < 3; r++) if (Math.abs(m[r][i]) > Math.abs(m[p][i])) p = r;
            var tmp = m[i]; m[i] = m[p]; m[p] = tmp;
            if (Math.abs(m[i][i]) < 1e-12) return [0, 0, 0];
            for (r = 0; r < 3; r++) { if (r === i) continue; var f = m[r][i] / m[i][i]; for (j = i; j < 4; j++) m[r][j] -= f * m[i][j]; }
        }
        return [m[0][3] / m[0][0], m[1][3] / m[1][1], m[2][3] / m[2][2]];
    }

    /* Усиления, при которых АЧХ трёх фильтров ближе всего к нужной кривой на
       13 центрах (1–16 кГц). АЧХ фильтра почти линейна по усилению — две
       поправки по истинной АЧХ каскада добирают нелинейность. */
    function fitGains(want, sr) {
        var g = { mid: 0, high: 0, air: 0 }, keys = ['mid', 'high', 'air'];
        if (!responseDb(sr, g, CENTERS)) return null;
        for (var pass = 0; pass < 3; pass++) {
            // базис: отклик на +1 дБ около текущей точки
            var J = [], k, a, b;
            var cur = Array.prototype.slice.call(responseDb(sr, g, CENTERS));
            for (a = 0; a < 3; a++) {
                var gp = { mid: g.mid, high: g.high, air: g.air }; gp[keys[a]] += 1;
                var rp = responseDb(sr, gp, CENTERS), col = [];
                for (k = 0; k < CENTERS.length; k++) col.push(rp[k] - cur[k]);
                J.push(col);
            }
            var S = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], B = [0, 0, 0];
            for (k = 0; k < CENTERS.length; k++) {
                var resid = want[k] - cur[k];
                for (a = 0; a < 3; a++) { B[a] += J[a][k] * resid; for (b = 0; b < 3; b++) S[a][b] += J[a][k] * J[b][k]; }
            }
            for (a = 0; a < 3; a++) S[a][a] += 0.02;            // гребень: фильтры перекрываются
            var d = solve3(S, B);
            for (a = 0; a < 3; a++) g[keys[a]] = clamp(g[keys[a]] + d[a], LIMITS.min, LIMITS.max);
        }
        for (var q = 0; q < 3; q++) g[keys[q]] = roundStep(g[keys[q]]);
        return g;
    }

    /* Линии графика: баланс каждой зоны по секундам, сглаженный за ±10 с
       (мощностью), относительно его уровня до «начала». «Станет» — то же
       плюс то, что сделают фильтры (их АЧХ, усреднённая по полосам зоны). */
    function curves(an, t0, P) {
        if (!an) return null;
        var n = an.nSec, out = { n: n, t0: t0, lines: [], after: P ? [] : null, late: [] };
        // «К концу» — последняя минута без последних 10 с (аутро/затухание
        // не должно решать, что человек прочтёт про свой трек).
        var lateA = Math.max(Math.floor(t0) + 5, an.end - 70), lateB = an.end - 10;
        var H = 10;
        for (var zi = 0; zi < ZONES.length; zi++) {
            var raw = new Float64Array(n), ok = new Uint8Array(n), t;
            for (t = 0; t < n; t++) if (an.valid[t]) { raw[t] = zoneBalance(an, zi, t); ok[t] = 1; }
            var sm = new Float64Array(n);
            for (t = 0; t < n; t++) {
                var acc = 0, c = 0;
                for (var u = Math.max(0, t - H); u <= Math.min(n - 1, t + H); u++) if (ok[u]) { acc += Math.pow(10, raw[u] / 10); c++; }
                sm[t] = c ? 10 * Math.log10(acc / c) : NaN;
            }
            var a0 = 0, ac = 0;
            for (t = Math.max(an.start + 5, 0); t < Math.min(n, Math.floor(t0)); t++) if (ok[t]) { a0 += Math.pow(10, raw[t] / 10); ac++; }
            var anchor = ac ? 10 * Math.log10(a0 / ac) : 0;
            var line = new Float64Array(n);
            for (t = 0; t < n; t++) line[t] = isFinite(sm[t]) && ok[t] ? sm[t] - anchor : NaN;
            out.lines.push(line);
            var la = 0, lc = 0;
            for (t = Math.max(0, lateA); t < Math.min(n, lateB); t++) if (ok[t]) { la += Math.pow(10, raw[t] / 10); lc++; }
            out.late.push(lc >= 10 && ac ? 10 * Math.log10(la / lc) - anchor : NaN);
            // Смещение под подгонку сюда не входит: график про то, как тембр
            // меняется ВО ВРЕМЕНИ, а статическую часть подгонки он не рисует.
            if (P) {
                var eff = zoneEffect(an.sr || 48000, P, zi), aft = new Float64Array(n);
                for (t = 0; t < n; t++) aft[t] = isFinite(line[t]) ? line[t] + eff * rampAt(P, t + 0.5) : NaN;
                out.after.push(aft);
            }
        }
        return out;
    }
    // Средняя по полосам зоны АЧХ трёх фильтров при полных усилениях P.gains.
    function zoneEffect(sr, P, zi) {
        var z = ZONES[zi], acc = 0, c = 0, r = responseDb(sr, P.gains, CENTERS);
        if (!r) return 0;
        for (var k = 0; k < CENTERS.length; k++) {
            if (CENTERS[k] >= z.lo * 0.89 && CENTERS[k] < z.hi * 1.12) { acc += r[k]; c++; }
        }
        return c ? acc / c : 0;
    }

    /* Рисует линии curves() на холсте — одна реализация на /music-mastering и
       /mix-analyzer. o: {t0, t1, dur, rtl, colors[3], muted, accent, dbLabel(v),
       startMark (вертикаль «начала» и заливка «до»)}. Сплошные — как есть,
       пунктир — «станет». Возвращает значения линий в конце (для подписи). */
    function drawCurves(cv, C, o) {
        if (!cv || !C) return null;
        var dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
        var W = cv.clientWidth || 600, Hh = cv.clientHeight || 150;
        cv.width = Math.round(W * dpr); cv.height = Math.round(Hh * dpr);
        var g = cv.getContext('2d'); if (!g) return null;
        g.clearRect(0, 0, cv.width, cv.height);
        var lo = -3, hi = 3, all = C.lines.concat(C.after || []);
        for (var a = 0; a < all.length; a++) for (var i = 0; i < all[a].length; i++) {
            var v = all[a][i]; if (isFinite(v)) { if (v < lo) lo = v; if (v > hi) hi = v; }
        }
        lo = Math.max(-15, Math.floor(lo - 0.5)); hi = Math.min(15, Math.ceil(hi + 0.5));
        var n = C.n, rtl = !!o.rtl, pad = 6 * dpr;
        function X(t) { var f = t / Math.max(1, n); return (rtl ? 1 - f : f) * cv.width; }
        function Y(db) { return pad + (hi - db) / (hi - lo) * (cv.height - 2 * pad); }
        if (o.startMark) {
            var xa = X(0), xs = X(o.t0);
            g.fillStyle = o.accent; g.globalAlpha = 0.08;
            g.fillRect(Math.min(xa, xs), 0, Math.abs(xs - xa), cv.height);
        }
        g.globalAlpha = 0.45; g.strokeStyle = o.muted; g.lineWidth = dpr; g.setLineDash([2 * dpr, 3 * dpr]);
        g.beginPath(); g.moveTo(0, Y(0)); g.lineTo(cv.width, Y(0)); g.stroke();
        g.setLineDash([]); g.globalAlpha = 1;
        if (o.startMark) {
            g.strokeStyle = o.accent; g.lineWidth = 2 * dpr;
            g.beginPath(); g.moveTo(X(o.t0), 0); g.lineTo(X(o.t0), cv.height); g.stroke();
            if (o.t1 < o.dur - 1) {
                g.setLineDash([4 * dpr, 4 * dpr]);
                g.beginPath(); g.moveTo(X(o.t1), 0); g.lineTo(X(o.t1), cv.height); g.stroke();
                g.setLineDash([]);
            }
        }
        function path(line, dash, color) {
            g.strokeStyle = color; g.lineWidth = (dash ? 1.5 : 2) * dpr; g.globalAlpha = dash ? 0.85 : 0.95;
            g.setLineDash(dash ? [5 * dpr, 4 * dpr] : []);
            g.beginPath(); var pen = false;
            for (var k = 0; k < line.length; k++) {
                if (!isFinite(line[k])) { pen = false; continue; }
                var x = X(k + 0.5), y = Y(line[k]);
                if (!pen) { g.moveTo(x, y); pen = true; } else g.lineTo(x, y);
            }
            g.stroke(); g.setLineDash([]); g.globalAlpha = 1;
        }
        for (var z = 0; z < C.lines.length; z++) path(C.lines[z], false, o.colors[z]);
        if (C.after) for (z = 0; z < C.after.length; z++) path(C.after[z], true, o.colors[z]);
        g.fillStyle = o.muted; g.font = Math.round(12 * dpr) + 'px system-ui, sans-serif';
        g.textAlign = rtl ? 'right' : 'left';
        var tx = rtl ? cv.width - 4 * dpr : 4 * dpr;
        g.textBaseline = 'top'; g.fillText(o.dbLabel(hi), tx, 2 * dpr);
        g.textBaseline = 'bottom'; g.fillText(o.dbLabel(lo), tx, cv.height - 2 * dpr);
        var ends = [];
        for (z = 0; z < C.lines.length; z++) {
            var ln = C.lines[z], last = NaN;
            for (var q = ln.length - 1; q >= 0; q--) if (isFinite(ln[q])) { last = ln[q]; break; }
            ends.push(last);
        }
        return ends;
    }

    var API = {
        ZONES: ZONES, LIMITS: LIMITS, DEFAULT_T0: DEFAULT_T0, MIN_DUR: MIN_DUR, CENTERS: CENTERS,
        resolve: resolve, withOffsets: withOffsets, averageOffset: averageOffset, rampAt: rampAt,
        addToGraph: addToGraph, makeStage: makeStage,
        analyzeAsync: analyzeAsync, suggest: suggest, curves: curves, zoneEffect: zoneEffect,
        cascadeDb: cascadeDb, drawCurves: drawCurves,
        // для стенда
        _rbj: rbj, _fitGains: fitGains, _finishAnalysis: finishAnalysis
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (typeof window !== 'undefined') window.MasteringTonalDrift = API;
})(typeof globalThis !== 'undefined' ? globalThis : this);
